import { Copy } from "lucide-react";
import styled from "styled-components";
import { Tooltip } from "@bracket/ui";

type ClipboardTextProps = {
	value: string;
	actual?: string;
	className?: string;
	color?: string;
	scale?: string;
	additions?: React.ReactNode;
};




/**
 * A component that displays text with a copy-to-clipboard icon
 * 
 * @param {ClipboardTextProps} props - The component props
 * @param {string} props.value - The text value to display
 * @param {string} [props.actual] - Optional text value to copy to clipboard (defaults to value)
 * @param {string} [props.className] - Optional CSS class name for styling
 * @param {string} [props.color] - Optional CSS color for the text and icon
 * @param {string} [props.scale] - Optional CSS font-size value (e.g., "1px", "2rem", "16px")
 * @returns {JSX.Element} A span element containing the text and copy icon
 */
export const ClipboardText = ({ value, actual, className, color, scale, additions }: ClipboardTextProps) => {

	const handleCopy = async (e: React.MouseEvent | React.KeyboardEvent) => {
		e.stopPropagation();
		e.preventDefault();
		try {
			await navigator.clipboard.writeText(actual || value);
		} catch (err) {
			console.error("Failed to copy text to clipboard:", err);
		}
	};

	return (
		<StyledContainer className={className} $color={color} $scale={scale}>
			<span className="address">{value}</span>
			<Icons>
			<Tooltip message="Copy to clipboard" position="top">
			
			<CopyIcon
				size={16}
				onClick={handleCopy}
				role="button"
				tabIndex={0}
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						handleCopy(e);
					}
				}}
				$color={color}
				$scale={scale}
			/>
			</Tooltip>
			{additions}
			</Icons>
		</StyledContainer>
	);
};


const StyledContainer = styled.span<{ $color?: string; $scale?: string }>`
	display: inline-flex;
	align-items: center;
	gap: 8px;
	user-select: none;
	-webkit-user-select: none;
	-moz-user-select: none;
	-ms-user-select: none;
	${props => props.$color && `color: ${props.$color};`}
	${props => props.$scale && `font-size: ${props.$scale};`}
	&.multiline {
		flex-direction: column;
	}

`;

const CopyIcon = styled(Copy)<{ $color?: string; $scale?: string }>`
	cursor: pointer;
	opacity: 0.7;
	transition: all 0.2s;
	color: var(--text-heading-title);
	${props => props.$color && `color: ${props.$color};`}
	${props => props.$scale && `width: ${props.$scale}; height: ${props.$scale};`}
	&:hover {
		opacity: 1;
		color: var(--primary);
	}
	&:focus {
		outline: none;
	}
`;

const Icons = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	width: 100%;
	justify-content: flex-end;
`;
